import { useState, useMemo, useCallback } from 'react';
import { isFormValid } from '../utils/validationUtils';
import { hasFormData, getInitialFormState } from '../utils/formUtils';
import { transformTaxonomyData } from '../utils/taxonomyUtils';
import type { ReviewFormData } from '../components/common/interfaces';
import { useProductService } from '@skin-seoul/service';

interface UseReviewFormOptions {
  isOpen: boolean;
}

interface UseReviewFormReturn {
  formState: ReviewFormData;
  formActions: {
    setRating: (value: number) => void;
    setTitle: (value: string) => void;
    setContent: (value: string) => void;
    setNickname: (value: string) => void;
    setSkinType: (value: string) => void;
    setSkinConcern: (value: string) => void;
    setImages: (value: File[]) => void;
    setRecommend: (value: string) => void;
  };
  formOptions: {
    skinTypeOptions: Array<{ value: string; label: string }>;
    skinConcernOptions: Array<{ value: string; label: string }>;
  };
  hasUnsavedChanges: boolean;
  isSubmitDisabled: boolean;
  resetForm: () => void;
}

export const useReviewForm = ({ isOpen }: UseReviewFormOptions): UseReviewFormReturn => {
  const [formState, setFormState] = useState<ReviewFormData>(getInitialFormState());
  const { useGetProductTaxonomy } = useProductService();

  const { data: taxonomy } = useGetProductTaxonomy({
    enabled: isOpen,
  });

  const { skinTypeOptions, skinConcernOptions } = transformTaxonomyData(taxonomy);

  // Form actions for cleaner prop passing
  const formActions = useMemo(
    () => ({
      setRating: (value: number) => setFormState((prev) => ({ ...prev, rating: value })),
      setTitle: (value: string) => setFormState((prev) => ({ ...prev, title: value })),
      setContent: (value: string) => setFormState((prev) => ({ ...prev, content: value })),
      setNickname: (value: string) => setFormState((prev) => ({ ...prev, nickname: value })),
      setSkinType: (value: string) => setFormState((prev) => ({ ...prev, skinType: value })),
      setSkinConcern: (value: string) => setFormState((prev) => ({ ...prev, skinConcern: value })),
      setImages: (value: File[]) => setFormState((prev) => ({ ...prev, images: value })),
      setRecommend: (value: string) => setFormState((prev) => ({ ...prev, recommend: value })),
    }),
    [],
  );

  const formOptions = {
    skinTypeOptions,
    skinConcernOptions,
  };

  // Derived state
  const hasUnsavedChanges = hasFormData(formState);
  const isSubmitDisabled = !isFormValid(formState);

  // Reset form utility
  const resetForm = useCallback(() => {
    setFormState(getInitialFormState());
  }, []);

  return {
    formState,
    formActions,
    formOptions,
    hasUnsavedChanges,
    isSubmitDisabled,
    resetForm,
  };
};
